import apiClient from "@/api/apiClient";
import { extractApiData } from "@/utils/apiResponseHandler";
import type {
  AssessmentPerson,
  AssessmentEntry,
  AssessmentListItem,
  AssessmentDetail,
  AssessmentForm,
  FinalGroupedData,
  FetchedAssessmentRisk,
  ConsequenceType,
  Risk,
  OrganisationScale,
} from "@/types";

class AssessmentAPI {
  private readonly model: string;

  constructor() {
    this.model = "assessment";
  }

  /**
   * Fetch all assessments.
   * @returns Promise resolving to an array of Assessment objects.
   */
  async fetchAllAssessments(): Promise<AssessmentListItem[]> {
    const response = await apiClient.get(`${this.model}`);
    return response.data.data;
  }

  /**
   * Fetch a single assessment by ID.
   * @param assessmentId ID of the assessment.
   * @returns Promise resolving to the Assessment object.
   */
  async fetchAssessmentById(assessmentId: number): Promise<AssessmentDetail> {
    const response = await apiClient.get(`${this.model}/${assessmentId}`);
    return response.data.data;
  }

  /**
   * Fetch persons associated with an assessment.
   * @param assessmentId ID of the assessment.
   * @returns Promise resolving to an array of person objects.
   */
  async fetchAssessmentPersons(
    assessmentId: number
  ): Promise<AssessmentPerson[]> {
    const response = await apiClient.get(
      `${this.model}/${assessmentId}/persons`
    );
    return response.data.data;
  }

  /**
   * Send a new assessment form to the server (creates assessment, persons, sends emails).
   * @param data Full assessment form payload.
   * @returns Promise resolving to server message and returned assessmentForm.
   */
  async createAssessment(
    data: AssessmentForm
  ): Promise<{ message: string; assessmentForm: AssessmentForm }> {
    return await apiClient.post(`${this.model}`, data).then(extractApiData);
  }

  /**
   * Fetch assessment risks for a participant by auth token.
   * @param payload Object containing authToken string.
   * @returns Promise resolving to message and data (risks, scales, types, flags).
   */
  async getAssessmentRisks(payload: { authToken: string }): Promise<{
    message: string;
    data: {
      risks: FetchedAssessmentRisk[];
      scales: OrganisationScale;
      consequenceTypes: ConsequenceType[];
      passExistingData: boolean;
      isSubmitted: boolean;
    };
  }> {
    return await apiClient
      .post(`${this.model}/risks`, payload)
      .then((response) => response.data);
  }

  /**
   * Save assessment entries with optional submission.
   * @param assessments Array of assessment entries.
   * @param authToken Authentication token.
   * @param submit Whether to submit after saving.
   * @returns Promise resolving to the saved data.
   */
  async saveAssessment(
    assessments: AssessmentEntry[],
    authToken: string,
    submit: boolean
  ): Promise<{ message: string }> {
    return await apiClient
      .post(`${this.model}/save`, {
        assessmentEntries: assessments,
        authToken,
        submit,
      })
      .then((response) => response.data);
  }

  /**
   * Finalise an assessment by submitting updated risk values.
   * @param assessmentId ID of the assessment.
   * @param payload Array of updated risk summary objects.
   * @returns Promise resolving to server acknowledgement message.
   */
  async finaliseAssessment(
    assessmentId: number,
    payload: Risk[]
  ): Promise<{ message: string }> {
    return await apiClient
      .post(`${this.model}/${assessmentId}/finalise`, { updatedRisks: payload })
      .then((response) => response.data);
  }

  /**
   * Save a single assessment risk suggestion (checked and suggested fields).
   * @param assessmentId ID of the assessment.
   * @param payload Full risk summary object (with .risk.id and .suggested fields).
   * @returns Promise resolving to server acknowledgement message.
   */
  async saveAssessmentRisk(
    assessmentId: number,
    payload: FinalGroupedData
  ): Promise<{ message: string }> {
    return await apiClient
      .post(`${this.model}/${assessmentId}/${payload.risk.id}/save`, payload)
      .then((response) => response.data);
  }

  /**
   * Delete a recipient from an assessment.
   * @param assessmentId ID of the assessment (unused by endpoint).
   * @param personId ID of the person to delete.
   * @returns Promise resolving to server acknowledgement.
   */
  async deleteAssessmentPerson(
    assessmentId: number,
    personId: number
  ): Promise<void> {
    return await apiClient
      .delete(`${this.model}/person/${personId}`)
      .then((response) => response.data);
  }

  /**
   * Delete an entry from an assessment.
   * @param entryId ID of the entry to delete.
   * @returns Promise resolving to deletion result.
   */
  async deleteAssessmentEntry(entryId: number): Promise<void> {
    return await apiClient
      .delete(`${this.model}/entry/${entryId}`)
      .then((response) => response.data);
  }
}

export default new AssessmentAPI();
